import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import WatchPreviewModal from "@/components/site/WatchPreviewModal";
import {
  WATCH_IMAGES,
  CLUB_EDITIONS,
  DESIGN_ARCHIVE,
  SCENIC_EDITIONS,
} from "@/lib/watchImages";
import {
  ALLOCATION_INTRO,
  COLLECTIONS,
  formatUSD,
  getCatalogLink,
} from "@/lib/allocation";

// Flat slug → watch lookup, drawn from every catalog source-array.
const WATCHES_BY_SLUG = [
  ...Object.values(WATCH_IMAGES),
  ...CLUB_EDITIONS,
  ...DESIGN_ARCHIVE,
  ...SCENIC_EDITIONS,
].reduce((acc, w) => {
  acc[w.slug] = w;
  return acc;
}, {});

function findWatch(catalogHref) {
  if (!catalogHref) return null;
  const slug = catalogHref.split("#")[1];
  return WATCHES_BY_SLUG[slug] || null;
}

export default function Allocation() {
  const [openWatch, setOpenWatch] = useState(null);
  return (
    <main
      data-testid="allocation-page"
      className="bg-[#0C0F0A] text-[#F5F2EB] min-h-screen relative overflow-hidden"
    >
      <Nav />

      {/* Header */}
      <section className="relative pt-40 sm:pt-48 pb-20 border-b border-[rgba(197,160,89,0.15)]">
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_55%_at_50%_-10%,rgba(197,160,89,0.10),transparent_60%)]" />
          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-transparent via-[rgba(197,160,89,0.15)] to-transparent" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="flex items-center justify-center gap-4">
            <span className="block w-12 h-px bg-[#C5A059]" />
            <span className="text-[10px] uppercase tracking-[0.42em] text-[#C5A059]">
              {ALLOCATION_INTRO.overline}
            </span>
            <span className="block w-12 h-px bg-[#C5A059]" />
          </div>

          <h1 className="mt-10 font-serif-display text-[#F5F2EB] font-light text-balance text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            {ALLOCATION_INTRO.title}
          </h1>

          <div className="mt-12 space-y-5 max-w-2xl mx-auto text-[#C9C6BC] text-[15px] sm:text-base leading-[1.85] font-light">
            {ALLOCATION_INTRO.body.map((p) => (
              <p key={p.slice(0, 40)}>{p}</p>
            ))}
          </div>

          <p className="mt-12 text-[11px] uppercase tracking-[0.32em] text-[#A8A9AD]/70">
            United States Dollars · Figures Pre-Launch
          </p>
        </div>
      </section>

      {/* Editorial tier sections */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 space-y-24 sm:space-y-32">
          {COLLECTIONS.map((c, i) => (
            <CollectionTier
              key={c.slug}
              collection={c}
              index={i}
              onPreview={setOpenWatch}
            />
          ))}
        </div>
      </section>

      {/* Closing strip */}
      <section className="relative border-t border-[rgba(197,160,89,0.18)] py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <p className="text-[#A8A9AD] text-sm leading-relaxed italic max-w-2xl mx-auto">
            {ALLOCATION_INTRO.footnote}
          </p>
          <Link
            to="/#waitlist"
            data-testid="allocation-cta"
            className="mt-12 inline-flex items-center justify-center gap-5 border border-[#C5A059] text-[#C5A059] px-12 py-4 text-[11px] uppercase tracking-[0.42em] hover:bg-[#C5A059] hover:text-[#0C0F0A] transition-colors duration-500"
          >
            Request an Allocation
            <span aria-hidden className="inline-block w-8 h-px bg-current" />
          </Link>
        </div>
      </section>

      <Footer />

      {/* Watch image lightbox — stays on the Allocation page */}
      <WatchPreviewModal
        watch={openWatch}
        onClose={() => setOpenWatch(null)}
        catalogHref={openWatch ? getCatalogLink(openWatch.name) : null}
      />
    </main>
  );
}

function CollectionTier({ collection: c, index, onPreview }) {
  return (
    <motion.div
      data-testid={`allocation-tier-${c.slug}`}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: "easeOut" }}
    >
      {/* Tier banner */}
      <div className="flex items-end justify-between flex-wrap gap-4 pb-6 border-b border-[rgba(197,160,89,0.25)]">
        <div>
          <div className="text-[10px] uppercase tracking-[0.42em] text-[#C5A059]">
            {String(index + 1).padStart(2, "0")} · Tier
          </div>
          <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#F5F2EB] font-light leading-[1.05]">
            {c.label}
          </h2>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.34em] text-[#A8A9AD] max-w-[20rem]">
            {c.cadence}
          </div>
        </div>
      </div>

      {/* Watch rows */}
      <ol className="mt-2 divide-y divide-[rgba(197,160,89,0.12)]">
        {c.watches.map((w, j) => (
          <WatchRow
            key={w.name}
            watch={w}
            index={j}
            tierSlug={c.slug}
            onPreview={onPreview}
          />
        ))}
      </ol>
    </motion.div>
  );
}

function WatchRow({ watch: w, index, tierSlug, onPreview }) {
  const isPrivate = w.privateAllocation;
  const priceLabel = isPrivate ? "By Private Allocation" : formatUSD(w.priceUSD);
  const allocLabel =
    typeof w.pieces === "number" ? `${w.pieces} pieces` : `${w.pieces} pieces`;
  const catalogHref = getCatalogLink(w.name);
  const previewWatch = findWatch(catalogHref);

  const handlePreview = (e) => {
    e.preventDefault();
    if (previewWatch && onPreview) onPreview(previewWatch);
  };

  return (
    <li
      data-testid={`allocation-row-${tierSlug}-${index}`}
      className="grid grid-cols-1 lg:grid-cols-12 gap-x-10 gap-y-5 py-8 sm:py-10"
    >
      {/* Left: serial + name */}
      <div className="lg:col-span-7">
        <div className="text-[10px] uppercase tracking-[0.42em] text-[#A8A9AD]">
          {String(index + 1).padStart(2, "0")} / Reference
        </div>
        {catalogHref ? (
          <button
            type="button"
            onClick={handlePreview}
            data-testid={`allocation-preview-${tierSlug}-${index}`}
            className="group/name mt-3 inline-flex items-baseline gap-3 font-serif-display text-2xl sm:text-3xl text-[#F5F2EB] font-light leading-tight tracking-tight hover:text-[#C5A059] transition-colors text-left bg-transparent border-0 p-0 cursor-pointer"
          >
            <span>{w.name}</span>
            <span
              aria-hidden
              className="text-[10px] uppercase tracking-[0.42em] text-[#C5A059]/70 group-hover/name:text-[#C5A059] transition-colors translate-y-[-2px]"
            >
              View →
            </span>
          </button>
        ) : (
          <h3 className="mt-3 font-serif-display text-2xl sm:text-3xl text-[#F5F2EB] font-light leading-tight tracking-tight">
            {w.name}
          </h3>
        )}
        <p className="mt-3 text-[#A8A9AD] text-sm sm:text-[15px] leading-relaxed max-w-xl">
          {w.direction}
        </p>
      </div>

      {/* Right: pricing block — spec-sheet styling */}
      <div className="lg:col-span-5 lg:pl-10 lg:border-l lg:border-[rgba(197,160,89,0.15)]">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.34em] text-[#C5A059]/85 mb-2">
              Target Figure
            </div>
            <div
              className={`font-serif-display ${
                isPrivate
                  ? "text-[#F5F2EB] text-lg sm:text-xl"
                  : "text-[#F5F2EB] text-xl sm:text-2xl"
              } font-light leading-tight tracking-tight`}
              data-testid={`allocation-price-${tierSlug}-${index}`}
            >
              {priceLabel}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.34em] text-[#C5A059]/85 mb-2">
              Allocation
            </div>
            <div className="font-serif-display text-xl sm:text-2xl text-[#F5F2EB] font-light leading-tight tracking-tight">
              {allocLabel}
            </div>
          </div>
        </div>
        {/* Scarcity visual — dot row indicating allocation density */}
        <ScarcityRail pieces={w.pieces} isPrivate={isPrivate} />
      </div>
    </li>
  );
}

function ScarcityRail({ pieces, isPrivate }) {
  // Render a horizontal hairline rail of dots that represents the allocation density.
  // Rarer allocations → fewer, more spaced dots, with one in gold.
  let dotCount = 24;
  let goldIndex = 11;

  if (isPrivate) {
    dotCount = 5;
    goldIndex = 0;
  } else if (typeof pieces === "number") {
    if (pieces <= 10) {
      dotCount = 10;
      goldIndex = 0;
    } else if (pieces <= 25) {
      dotCount = 14;
      goldIndex = 0;
    } else if (pieces <= 50) {
      dotCount = 18;
      goldIndex = 1;
    } else if (pieces <= 100) {
      dotCount = 22;
      goldIndex = 2;
    } else if (pieces <= 250) {
      dotCount = 26;
      goldIndex = 4;
    } else {
      dotCount = 30;
      goldIndex = 6;
    }
  }

  return (
    <div
      aria-hidden
      className="mt-7 flex items-center gap-1.5"
      data-testid="allocation-scarcity-rail"
    >
      {Array.from({ length: dotCount }).map((_, i) => (
        <span
          key={`dot-${i}`}
          className={`block w-1 h-1 rounded-full ${
            i === goldIndex
              ? "bg-[#C5A059]"
              : "bg-[rgba(245,242,235,0.18)]"
          }`}
        />
      ))}
    </div>
  );
}


